import * as React from 'react';
import isEqual from 'react-fast-compare';
import { useId } from 'react-id-generator';
import { FormProvider } from './FormContext/FormContext.js';
import { jsx } from 'react/jsx-runtime';

function Component(props, ref) {
  var _props$id;
  var defaultValues = props.defaultValues,
    _props$reValidateMode = props.reValidateMode,
    reValidateMode = _props$reValidateMode === void 0 ? "onBlur" : _props$reValidateMode,
    _props$validateMode = props.validateMode,
    validateMode = _props$validateMode === void 0 ? "onSubmit" : _props$validateMode;
  var formControlsRef = React.useRef({});
  var generatedId = useId(1, "form-")[0];
  var formId = (_props$id = props.id) !== null && _props$id !== void 0 ? _props$id : generatedId;
  var isSubmittedRef = React.useRef(false);
  var defaultValuesRef = React.useRef(defaultValues);
  var errors = React.useRef({});
  var setFormControlRef = React.useCallback(function (name, onUpdateValue, onValidate, value) {
    formControlsRef.current[name] = {
      value: value,
      onUpdateValue: onUpdateValue,
      onValidate: onValidate
    };
  }, []);
  var clearFormControlRef = React.useCallback(function (name) {
    delete formControlsRef.current[name];
  }, []);
  var getValues = React.useCallback(function () {
    var values = {};
    Object.keys(formControlsRef.current).forEach(function (key) {
      var value = formControlsRef.current[key].value;
      if (value !== void 0) {
        assignValueToNestedObject(values, key, value);
      }
    });
    return values;
  }, []);
  var validate = React.useCallback(function () {
    var _values = getValues();
    var _errors = {};
    Object.keys(formControlsRef.current).forEach(function (key) {
      var fieldValue = formControlsRef.current[key].value;
      var errorValue = formControlsRef.current[key].onValidate(fieldValue, _values);
      if (errorValue) {
        assignValueToNestedObject(_errors, key, errorValue);
      }
    });
    if (!isEqual(errors.current, _errors)) {
      errors.current = _errors;
      if (props.onValidationError) {
        props.onValidationError(_errors);
      }
    }
    return Object.keys(_errors).length === 0;
  }, [getValues, props.onValidationError]);
  var onChange = React.useCallback(function (name, value) {
    formControlsRef.current[name].value = value;
    if (validateMode === "all" || !isSubmittedRef.current && validateMode === "onChange" || isSubmittedRef.current && reValidateMode === "onChange") {
      if (!validate()) {
        return;
      }
    }
    if (props.onChange) {
      props.onChange(getValues());
    }
  }, [validate, validateMode, reValidateMode, props.onChange]);
  var onBlur = React.useCallback(function (event) {
    if (validateMode === "all" || validateMode === "onBlur" || isSubmittedRef.current && reValidateMode === "onBlur") {
      validate();
    }
    if (props.onBlur) {
      props.onBlur(event);
    }
  }, [validateMode, reValidateMode, validate, props.onBlur]);
  var handleSubmit = React.useCallback(function (onSubmit) {
    isSubmittedRef.current = true;
    if ((validateMode === "all" || validateMode === "onSubmit") && !validate()) {
      return;
    }
    if (onSubmit) {
      onSubmit(getValues());
    } else if (props.onSubmit) {
      props.onSubmit(getValues());
    }
  }, [validate, validateMode, props.onSubmit]);
  var setToDefaultValues = React.useCallback(function (resetValues) {
    var _resetValues = resetValues !== null && resetValues !== void 0 ? resetValues : defaultValuesRef.current;
    Object.keys(formControlsRef.current).forEach(function (formControlName) {
      var newValue = _resetValues[formControlName];
      formControlsRef.current[formControlName].value = newValue;
      formControlsRef.current[formControlName].onUpdateValue(newValue);
    });
  }, [getValues, props.onChange]);
  var _onReset = React.useCallback(function (values) {
    setToDefaultValues(values);
    if (props.onChange) {
      props.onChange(getValues());
    }
  }, [getValues, props.onChange, setToDefaultValues]);
  React.useEffect(function () {
    defaultValuesRef.current = defaultValues;
  }, [defaultValues]);
  React.useImperativeHandle(ref, function () {
    return {
      handleSubmit: handleSubmit,
      reset: _onReset,
      trigger: validate
    };
  });
  return /*#__PURE__*/jsx(FormProvider, {
    clearFormControlRef: clearFormControlRef,
    defaultValuesRef: defaultValuesRef,
    disableAll: props.disableAll,
    formId: formId,
    inlineAll: props.inlineAll,
    onChange: onChange,
    setFormControlRef: setFormControlRef,
    children: /*#__PURE__*/jsx("form", {
      id: formId,
      onBlur: onBlur,
      onSubmit: function onSubmit(e) {
        e.preventDefault();
        handleSubmit();
      },
      onReset: function onReset() {
        return _onReset();
      },
      style: props.style,
      children: props.children
    })
  });
}
Component.displayName = "FormContainerV2";
var FormContainerV2 = /*#__PURE__*/React.forwardRef(Component);
function assignValueToNestedObject(base, propName, value) {
  var nameSplitOnDot = propName.split(".");
  var lastName = nameSplitOnDot[nameSplitOnDot.length - 1];
  nameSplitOnDot.forEach(function (name) {
    base = base[name] = name === lastName ? value : base[name] || {};
  });
  return base;
}

export { FormContainerV2 };
